import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { ApiClientConfig } from '../../api-client-config';
import { map } from 'rxjs/operators';
import {
  CommonDocumentEditorDetails,
  DocRenameRequest,
  DocumentDuplicateResponse,
  DocumentLockResponse,
  DpiaEvaluationMap,
  TreatmentDependency,
  TreatmentDocCreateRequest,
  TreatmentDocUpdateRequest,
  TreatmentOverrideOnDuplicate,
} from '@primatoo-ms-organization';
import { LegalEntity } from '@ms-organization-entities';
import { ApiClientUtils } from '../../../api-client.utils';
import { BusinessProjectManagementService } from '../../interface/business-project-management-service';
import { GetTreatPermissionResponse } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/GetTreatPermissionResponse';
import { UpdateTreatPermissionRequest } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/UpdateTreatPermissionRequest';
import { GetTreatAvailableOperationsResponse } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/GetTreatAvailableOperationsResponse';
import { TreatmentType } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/TreatmentType';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { DownloadRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/DownloadRequest';
import { ListTemplateChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse';
import { GetTreatmentForEditorDetailResponse } from 'it/primatoo/openapi/api/org/schemas/treatment/GetTreatmentForEditorDetailResponse';
import { ListDocumentResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/ListDocumentResponse';
import { DocResourceStatus } from 'it/primatoo/openapi/api/org/schemas/common/DocResourceStatus';
import { GetTreatmentForDetailsResponse } from 'it/primatoo/openapi/api/org/schemas/treatment/GetTreatmentForDetailsResponse';
import { CreateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse';
import { DocumentItem } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listdocumentresponse/DocumentItem';
import { UpdateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse';
import { ListUserColumnsResponse } from 'it/primatoo/openapi/api/org/schemas/common/ListUserColumnsResponse';
import { UpsertUserColumnRequest } from 'it/primatoo/openapi/api/org/schemas/common/UpsertUserColumnRequest';
import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';
import { ListObsoleteDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/ListObsoleteDocResponse';
import { SetObsoleteRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SetObsoleteRequest';
import { GetTreatmentLiaRequiredResponse } from 'it/primatoo/openapi/api/org/schemas/treatment/GetTreatmentLiaRequiredResponse';
import { DuplicateAnalysisResponse } from 'it/primatoo/openapi/api/org/schemas/treatment/DuplicateAnalysisResponse';
import {
  TreatmentDuplicateAnalysisRequest
} from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentDuplicateAnalysisRequest';

@Injectable({
  providedIn: 'root',
})
export class TreatmentService extends BusinessProjectManagementService {
  private readonly endpointV1: string;
  private readonly endpointV2: string;
  private readonly docsEndpoint: string;
  private readonly docsV2Endpoint: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient
  ) {
    super(httpClient);
    this.endpointV1 = this.config.endPoint + '/api/org/treatments/';
    this.endpointV2 = this.config.endPoint + '/api/org/v2/treatments/';
    this.docsEndpoint = this.config.endPoint + '/api/org/docs/treatments/';
    this.docsV2Endpoint = this.config.endPoint + '/api/org/v2/docs/treatments/';
  }

  getEndPointV1(): string {
    return this.endpointV1;
  }

  getEndPointV2(): string {
    return this.endpointV2;
  }

  // ----------------------------------- API DOC V2 ----------------------------------- //

  // ----------------------------------- API V2 ----------------------------------- //

  duplicateAnalysis(id: number): Observable<DuplicateAnalysisResponse> {
    const request: TreatmentDuplicateAnalysisRequest = {
      targetType: TreatmentType.HOLDER,
    };
    return this.httpClient.post<DuplicateAnalysisResponse>(
      this.endpointV2 + `${id}/latest/duplicate-analysis`,
      request
    );
  }
  duplicateHolderToResponsibleAnalysis(id: number): Observable<DuplicateAnalysisResponse> {
    const request: TreatmentDuplicateAnalysisRequest = {
      targetType: TreatmentType.RESPONSIBLE,
    };
    return this.httpClient.post<DuplicateAnalysisResponse>(
      this.endpointV2 + `${id}/latest/duplicate-analysis`,
      request
    );
  }

  getTreatmentTemplateChapterId(
    id: number,
    chapterId: number
  ): Observable<GetChapterResponse> {
    return this.httpClient.get<GetChapterResponse>(
      this.endpointV2 + `${id}/latest/template/chapters/${chapterId}/`
    );
  }

  getDocumentTemplateChapters(
    id: number
  ): Observable<ListTemplateChapterResponse> {
    return this.httpClient.get<ListTemplateChapterResponse>(
      this.endpointV2 + `${id}/latest/template/chapters/`
    );
  }

  preview(id: number, request?: DownloadRequest) {
    const url = this.downloadPreviewPdfUrl(id);
    return this.httpClient.post(
      url,
      request ? request : { chapterSelectors: [] },
      { responseType: 'text' }
    );
  }

  /**
   * API di download multiplo. necessario chiamare l'api GET /templates/name/{name}/chapters/ per la lista dei capitoli
   * @param id
   * @param request
   */
  // submitDownload(id: number, request: SubmitDownloadRequest): Observable<SubmitDownloadResponse> {
  //   const url = this.endpointV2 + `${id}/latest/submit-download`;
  //   return this.httpClient.post<SubmitDownloadResponse>(url, request);
  // }

  findAllFiltered(
    filter?: string,
    filterByAnyPermission = false,
    columns?: string[],
    selectedLegalEntities?: LegalEntity[],
    treatmentType?: TreatmentType
  ): Observable<Array<DocumentItem>> {
    let url = this.endpointV2 + `latest/`;
    switch (treatmentType) {
      case TreatmentType.HOLDER:
        url += `?q=treatmentType::${TreatmentType.HOLDER}`;
        break;
      case TreatmentType.RESPONSIBLE:
        url += `?q=treatmentType::${TreatmentType.RESPONSIBLE}`;
        break;
      case TreatmentType.SUB_RESPONSIBLE:
        url += `?q=treatmentType::${TreatmentType.SUB_RESPONSIBLE}`;
        break;
      case TreatmentType.PROJECT:
        url += `?q=treatmentType::${TreatmentType.PROJECT}`;
        break;
      default:
        return EMPTY;
    }
    url += filter ? ',' + filter : '';

    if (selectedLegalEntities?.length > 0) {
      if (!columns.includes('status')) {
        columns.push('status');
      }
      if (!columns.includes('legalEntityId')) {
        columns.push('legalEntityId');
      }
      url += `,${ApiClientUtils.buildLegalEntitiesFilter(
        selectedLegalEntities
      )}`;
    }

    if (columns && columns.length > 0) {
      url += '&columns=' + columns.join('%7C'); //columns.join('|');
    }
    url += '&sort=docId,DESC';
    url += filterByAnyPermission
      ? '&filterByAnyPermission=true'
      : '&filterByAnyPermission=false';

    return this.httpClient.get<ListDocumentResponse>(url).pipe(
      map((response) => {
        return response?.documents;
      })
    );
  }

  // ----------------------------------- API V1 ----------------------------------- //
  getDocColumn(type: TreatmentType): Observable<ColumnWithLocalization[]> {
    const url = this.docsEndpoint + `columns/type/${type}/`;
    return this.httpClient.get<ColumnWithLocalization[]>(url);
  }

  create(body: TreatmentDocCreateRequest): Observable<CreateDocResponse> {
    const url = this.docsEndpoint;
    return this.httpClient.post<CreateDocResponse>(url, body);
  }

  delete(id: number): Observable<void> {
    const url = this.docsEndpoint + `${id}/`;
    return this.httpClient.delete<void>(url);
  }

  update(
    id: number,
    body: TreatmentDocUpdateRequest
  ): Observable<UpdateDocResponse> {
    const url = this.docsEndpoint + `${id}/`;
    return this.httpClient.put<UpdateDocResponse>(url, body);
  }

  rename(id: number, request: DocRenameRequest): Observable<void> {
    const url = this.docsEndpoint + `${id}/rename`;
    return this.httpClient.patch<void>(url, request);
  }

  getToOverride(
    treatmentType: TreatmentType
  ): Observable<TreatmentOverrideOnDuplicate> {
    const url = this.endpointV1 + `override-on-duplicate?type=${treatmentType}`;
    return this.httpClient.get<TreatmentOverrideOnDuplicate>(url);
  }

  duplicate(
    id: number,
    request: TreatmentDocCreateRequest
  ): Observable<DocumentDuplicateResponse> {
    const url = this.endpointV1 + `${id}/latest/duplicate`;
    return this.httpClient.post<DocumentDuplicateResponse>(url, request);
  }

  reopen(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/re-open`;
    return this.httpClient.post<void>(url, {});
  }

  getDetails(id: number): Observable<GetTreatmentForDetailsResponse> {
    const url = this.endpointV2 + `${id}/latest/details`;
    return this.httpClient.get<GetTreatmentForDetailsResponse>(url);
  }

  getEditorDetails(id: number): Observable<CommonDocumentEditorDetails> {
    const url = this.endpointV2 + `${id}/latest/editor-details/`;
    return this.httpClient.get<GetTreatmentForEditorDetailResponse>(url).pipe(
      map((m) => {
        const response: CommonDocumentEditorDetails = {
          docId: m.docId,
          status: m.status as DocResourceStatus,
          statusLabel: m.statusLabel,
          name: m.name,
          userCheck: m.userCheck,
          description: m.description,
          internalId: m.internalId,
          parentId: m.parentId,
          parentName: m.parentName,
          completed: m.completed,
          creatorUserId: m.creatorUserId,
          updatedAt: m.updatedAt,
          warnings: m.warnings,
          treatmentCategoryId: m.treatmentCategoryId,
          treatmentCategoryName: m.treatmentCategoryName,
          treatmentType: m.treatmentType,
          legalEntityId: m.legalEntityId,
          legalEntityName: m.legalEntityName,
          treatmentRiskFactors: m.treatmentRiskFactors,
          departmentId: m.departmentId,
          processOwnerId: m.processOwnerId,
        };
        return response;
      })
    );
  }

  // getCyberSecDetails(id: number): Observable<TreatmentForCyberSecDetailResponse> {
  //   const url = this.endpointV2 + `${id}/latest/cyber-sec/`;
  //   return this.httpClient.get<TreatmentForCyberSecDetailResponse>(url);
  // }

  getDependency(id: number): Observable<TreatmentDependency> {
    const url = this.endpointV1 + `${id}/latest/dependency`;
    return this.httpClient.get<TreatmentDependency>(url);
  }

  upsertWidgets(id: number, widgets: UpsertWidgetsRequest): Observable<void> {
    const url = this.endpointV2 + `${id}/latest/widgets/`;
    return this.httpClient.post<void>(url, widgets);
  }

  releaseLock(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/lock`;
    return this.httpClient.delete<void>(url, {});
  }

  lockResource(id: number, token: number): Observable<DocumentLockResponse> {
    let url = this.endpointV1 + `${id}/latest/lock`;
    if (token !== null) {
      url = `${url}?token=${token}`;
    }
    return this.httpClient.post<DocumentLockResponse>(url, {});
  }

  publish(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/publish`;
    return this.httpClient.post<void>(url, {});
  }


  refreshResults(id: number): Observable<any> {
    const url = this.endpointV1 + `${id}/latest/refresh-results`;
    return this.httpClient.post<any>(url, {});
  }

  deleteMultiple(ids: number[]): Observable<{ failed: number[] }> {
    const url = this.docsEndpoint + `by-ids`;
    // return this.httpClient.request<{ failed: number[] }>('DELETE', url, {body: {ids}})
    return this.httpClient.post<{ failed: number[] }>(url, { ids });
  }

  userChecked(id: number): Observable<void> {
    const url = this.endpointV1 + `${id}/latest/user-checked`;
    return this.httpClient.post<void>(url, {});
  }

  getDocUserPermission(
    docId: number
  ): Observable<GetTreatAvailableOperationsResponse> {
    const url = this.endpointV2 + `${docId}/latest/user-operations/`;
    return this.httpClient.get<GetTreatAvailableOperationsResponse>(url);
  }

  getActivePermission(docId: number): Observable<GetTreatPermissionResponse> {
    const url = this.docsV2Endpoint + `${docId}/permissions/`;
    return this.httpClient.get<GetTreatPermissionResponse>(url);
  }

  patchActivePermission(
    docId: number,
    request: UpdateTreatPermissionRequest
  ): Observable<void> {
    const url = this.docsV2Endpoint + `${docId}/permissions/`;
    return this.httpClient.patch<void>(url, request);
  }

  getDpiaEvalutationMap(): Observable<DpiaEvaluationMap> {
    const url = this.endpointV1 + `dpias/evaluation-map/`;
    return this.httpClient.get<DpiaEvaluationMap>(url);
  }

  /**
   * DPIA
   */

  findAllDpiaFiltered(
    filter?: string,
    filterByAnyPermission = false,
    selectedLegalEntities?: LegalEntity[],
    columns?: string[]
  ): Observable<Array<DocumentItem>> {
    let url = this.endpointV2 + `latest/?q=hasDpia::true`;
    url += filter ? ',' + filter : '';

    if (selectedLegalEntities?.length > 0) {
      if (!columns.includes('status')) {
        columns.push('status');
      }
      if (!columns.includes('legalEntityId')) {
        columns.push('legalEntityId');
      }
      url += `,${ApiClientUtils.buildLegalEntitiesFilter(
        selectedLegalEntities
      )}`;
    }

    // if (selectedLegalEntities?.length > 0) {
    //   url += `,${ApiClientUtils.buildLegalEntitiesFilter(selectedLegalEntities)}`;
    // }
    if (columns && columns.length > 0) {
      url += '&columns=' + columns.join('%7C'); //columns.join('|');
    }

    url += '&sort=docId,DESC';
    url += filterByAnyPermission
      ? '&filterByAnyPermission=true'
      : '&filterByAnyPermission=false';

    return this.httpClient.get<ListDocumentResponse>(url).pipe(
      map((response) => {
        return response?.documents;
      })
    );
  }

  findAllLiaFiltered(
    filter?: string,
    filterByAnyPermission = false,
    selectedLegalEntities?: LegalEntity[],
    columns?: string[]
  ): Observable<Array<DocumentItem>> {
    let url = this.endpointV2 + `latest/?q=hasLia::true`;
    url += filter ? ',' + filter : '';

    if (selectedLegalEntities?.length > 0) {
      if (!columns.includes('status')) {
        columns.push('status');
      }
      if (!columns.includes('legalEntityId')) {
        columns.push('legalEntityId');
      }
      url += `,${ApiClientUtils.buildLegalEntitiesFilter(
        selectedLegalEntities
      )}`;
    }
    if (columns && columns.length > 0) {
      url += '&columns=' + columns.join('%7C'); //columns.join('|');
    }

    url += '&sort=docId,DESC';
    url += filterByAnyPermission
      ? '&filterByAnyPermission=true'
      : '&filterByAnyPermission=false';

    return this.httpClient.get<ListDocumentResponse>(url).pipe(
      map((response) => {
        return response?.documents;
      })
    );
  }
  public getLiaColumn(): Observable<ColumnWithLocalization[]> {
    const url = this.getEndPointV2() + `columns/lia/`;
    return this.httpClient
      .get<ListUserColumnsResponse>(url)
      .pipe(map((c) => c.columns));
  }

  public setLiaColumns(request: UpsertUserColumnRequest): Observable<void> {
    const url = this.getEndPointV2() + `user-columns/lia/`;
    return this.httpClient.post<void>(url, request);
  }

  public getDpiaColumn(): Observable<ColumnWithLocalization[]> {
    const url = this.getEndPointV2() + `columns/dpia/`;
    return this.httpClient
      .get<ListUserColumnsResponse>(url)
      .pipe(map((c) => c.columns));
  }

  public setDpiaColumns(request: UpsertUserColumnRequest): Observable<void> {
    const url = this.getEndPointV2() + `user-columns/dpia/`;
    return this.httpClient.post<void>(url, request);
  }

  public getObsolete(
    treatmentType: TreatmentType,
    legalEntityIds: number[]
  ): Observable<ListObsoleteDocResponse> {
    let url = this.getEndPointV2() + `obsolete/type/${treatmentType}/`;

    if (legalEntityIds?.length > 0) {
      const q = `legalEntityId_in_${legalEntityIds.join('|')}`;
      url += `?q=${encodeURIComponent(q)}`;
    }

    return this.httpClient.get<ListObsoleteDocResponse>(url);
  }

  public makeObsolete(docId: number, note: string): Observable<void> {
    const body: SetObsoleteRequest = {
      description: note,
    };
    const url = this.getEndPointV2() + `${docId}/make-obsolete`;
    return this.httpClient.post<void>(url, body);
  }

  liaRequired(docId: number): Observable<GetTreatmentLiaRequiredResponse> {
    const url = this.getEndPointV2() + `${docId}/lia-required/`;
    return this.httpClient.get<GetTreatmentLiaRequiredResponse>(url);
  }
}
